import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import StartPrintDialog from '../../StartPrintDialog';
import { generateJobId } from '../../utils/printJobs';
import { showToastError } from '../../components/ToastNotifications';
import { useLegacyAppSession } from '../../contexts/LegacyAppSessionContext';
import { useRestoredPrintPackage } from '../../hooks/usePackages';
import { useDeleteDraftPrintRun } from '../../hooks/usePrintRuns';

export default function NewStagedPrintContainer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, org } = useLegacyAppSession();
  const organizationId = org?.id == null ? undefined : Number(org.id);
  const { deleteDraft, isDeletingDraft } = useDeleteDraftPrintRun(organizationId);
  const pkg = location.state?.pkg || null;
  const routedFacilityId = Number(pkg?.facilityId);
  const hasRoutedFacility = Number.isSafeInteger(routedFacilityId) && routedFacilityId > 0;
  const {
    data: initialPackage,
    isLoading: isPackageLoading,
    error: packageError,
    retry: retryPackage,
  } = useRestoredPrintPackage(org?.id, {
    isStaged: true,
    packageLabel: pkg?.packageLabel,
    facilityId: hasRoutedFacility ? routedFacilityId : undefined,
  });

  useEffect(() => {
    if (packageError) {
      showToastError('Staged package could not be loaded.');
    }
  }, [packageError]);

  if (isPackageLoading) return null;
  if (packageError) {
    return (
      <div role="alert">
        <p>Staged package could not be loaded.</p>
        <button type="button" onClick={retryPackage}>Retry</button>
        <button type="button" onClick={() => navigate('/prints', { replace: true })}>Back to Print Runs</button>
      </div>
    );
  }

  const stagedPackage = initialPackage as {
    id?: number | string;
    packageLabel?: string;
    label?: string;
    facilityId?: number | string;
    stagedCoaId?: number | string;
    stagedCoaData?: string | null;
  } | null;
  const stagedPackageLabel = stagedPackage?.packageLabel || stagedPackage?.label || pkg?.packageLabel;
  const currentStagedCoaId = Number(stagedPackage?.stagedCoaId);
  if (pkg && (!hasRoutedFacility
    || Number(stagedPackage?.facilityId) !== routedFacilityId
    || !Number.isSafeInteger(currentStagedCoaId)
    || currentStagedCoaId <= 0)) {
    return (
      <div role="alert">
        <p>Staged COA could not be loaded.</p>
        <button type="button" onClick={retryPackage}>Retry</button>
        <button type="button" onClick={() => navigate('/prints', { replace: true })}>Back to Print Runs</button>
      </div>
    );
  }
  const currentStagedCoaData = stagedPackage?.stagedCoaData ?? undefined;

  return (
    <StartPrintDialog
      defaultJobId={generateJobId()}
      initialPackage={stagedPackage}
      initialValues={pkg ? {
        packageTag: stagedPackageLabel,
        isStaged: true,
        stagedCoaId: currentStagedCoaId,
        stagedCoaData: currentStagedCoaData,
        stagedPackage: {
          id: currentStagedCoaId,
          packageLabel: stagedPackageLabel,
          data: currentStagedCoaData,
        },
      } : undefined}
      onClose={() => navigate('/prints', { replace: true })}
      onSave={async () => {
        navigate('/prints', { replace: true });
      }}
      onDeleteDraft={deleteDraft}
      isDeletingDraft={isDeletingDraft}
      organizationId={organizationId}
      currentUser={currentUser}
    />
  );
}
